import {
  Component,
  JSXElement,
  Show,
  createSignal,
  createEffect,
  onMount,
  onCleanup,
} from 'solid-js';
import Popup from '../Popup/Popup';
import Spinner from '../Spinner/Spinner';
import './ImageCropper.css';

export interface ICrop {
  x: number;
  y: number;
  unit: '%';
  width: number;
  aspect: number;
}

export interface ImageCropperProps {
  srcImageUrl: string;
  onSavePhoto(cropped: boolean, file: File | null): void;
  heading?: string;
  saveLabel?: string;
  cancelLabel?: string;
  savingLabel?: string;
}

const PERCENT = 100;
const CROP_MIN_WIDTH_PCT = 10;
const CROP_INITIAL_X_PCT = 15;
const CROP_INITIAL_Y_PCT = 15;
const CROP_INITIAL_WIDTH_PCT = 50;
const MIN_BLOB_SIZE_BYTES = 300;
const PREVIEW_DEBOUNCE_MS = 100;

const INITIAL_CROP = {
  x: CROP_INITIAL_X_PCT,
  y: CROP_INITIAL_Y_PCT,
  width: CROP_INITIAL_WIDTH_PCT,
};

const ImageCropper: Component<ImageCropperProps> = (props): JSXElement => {
  let imgRef: HTMLImageElement | undefined;
  let cropAreaRef: HTMLDivElement | undefined;

  const [showPopup, setShowPopup] = createSignal(false);
  const [src, setSrc] = createSignal('');
  const [isSaving, setIsSaving] = createSignal(false);
  const [previewSrc, setPreviewSrc] = createSignal('');
  const [showPreview, setShowPreview] = createSignal(false);
  const [previewLoading, setPreviewLoading] = createSignal(true);
  const [croppedFile, setCroppedFile] = createSignal<File | null>(null);

  const [crop, setCrop] = createSignal(INITIAL_CROP);
  const [dragging, setDragging] = createSignal(false);
  const [resizing, setResizing] = createSignal(false);
  const [dragStart, setDragStart] = createSignal({
    mx: 0,
    my: 0,
    cx: 0,
    cy: 0,
  });

  createEffect(() => {
    if (props.srcImageUrl) {
      setSrc(props.srcImageUrl);
      setShowPopup(true);
      setShowPreview(false);
      setPreviewLoading(true);
      setCrop(INITIAL_CROP);
    }
  });

  function getImageDimensions(): { iw: number; ih: number } {
    if (!imgRef || !cropAreaRef) return { iw: 0, ih: 0 };
    return { iw: imgRef.clientWidth, ih: imgRef.clientHeight };
  }

  function cropToPixels(): { x: number; y: number; size: number } {
    const { iw, ih } = getImageDimensions();
    const c = crop();
    return {
      x: (c.x / PERCENT) * iw,
      y: (c.y / PERCENT) * ih,
      size: (c.width / PERCENT) * Math.min(iw, ih),
    };
  }

  function handleCropMouseDown(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    const c = crop();
    setDragStart({ mx: e.clientX, my: e.clientY, cx: c.x, cy: c.y });
  }

  function handleResizeMouseDown(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    const c = crop();
    setDragStart({ mx: e.clientX, my: e.clientY, cx: c.width, cy: 0 });
  }

  function handleMouseMove(e: MouseEvent): void {
    if (!imgRef) return;
    const { iw, ih } = getImageDimensions();
    if (iw === 0 || ih === 0) return;

    if (dragging()) {
      const ds = dragStart();
      const dx = ((e.clientX - ds.mx) / iw) * PERCENT;
      const dy = ((e.clientY - ds.my) / ih) * PERCENT;
      const c = crop();
      const minDim = Math.min(iw, ih);
      const sizePctW = (((c.width / PERCENT) * minDim) / iw) * PERCENT;
      const sizePctH = (((c.width / PERCENT) * minDim) / ih) * PERCENT;
      const newX = Math.max(0, Math.min(PERCENT - sizePctW, ds.cx + dx));
      const newY = Math.max(0, Math.min(PERCENT - sizePctH, ds.cy + dy));
      setCrop({ ...c, x: newX, y: newY });
    }

    if (resizing()) {
      const ds = dragStart();
      const minDim = Math.min(iw, ih);
      const dw = ((e.clientX - ds.mx) / minDim) * PERCENT;
      const newWidth = Math.max(
        CROP_MIN_WIDTH_PCT,
        Math.min(PERCENT, ds.cx + dw)
      );
      setCrop((prev) => ({ ...prev, width: newWidth }));
    }
  }

  function handleMouseUp(): void {
    if (dragging() || resizing()) {
      setDragging(false);
      setResizing(false);
      generatePreview();
    }
  }

  onMount(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  });

  onCleanup(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  });

  async function generatePreview(): Promise<void> {
    if (!imgRef) return;
    const { iw, ih } = getImageDimensions();
    if (iw === 0 || ih === 0) return;

    const scaleX = imgRef.naturalWidth / iw;
    const scaleY = imgRef.naturalHeight / ih;
    const px = cropToPixels();

    const canvas = document.createElement('canvas');
    const size = Math.ceil(px.size * scaleX);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      imgRef,
      px.x * scaleX,
      px.y * scaleY,
      px.size * scaleX,
      px.size * scaleY,
      0,
      0,
      size,
      size
    );

    return new Promise<void>((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob || blob.size < MIN_BLOB_SIZE_BYTES) {
          resolve();
          return;
        }
        const file = new File([blob], 'previewFile.png', { type: 'image/png' });
        setCroppedFile(() => file);
        setPreviewSrc(URL.createObjectURL(blob));
        setPreviewLoading(false);
        setShowPreview(true);
        resolve();
      }, 'image/png');
    });
  }

  function handleImageLoad(): void {
    setCrop(INITIAL_CROP);
    setShowPreview(false);
    setPreviewSrc('');
    setPreviewLoading(true);
    setTimeout(() => void generatePreview(), PREVIEW_DEBOUNCE_MS);
  }

  function handleSave(): void {
    setIsSaving(true);
    const file = croppedFile();
    if (file) {
      props.onSavePhoto(true, file);
    } else {
      props.onSavePhoto(false, null);
    }
    setShowPopup(false);
    setIsSaving(false);
  }

  function handleClose(): void {
    setShowPopup(false);
  }

  const cropperContent = (): JSXElement => (
    <div class="ic-cropper-row" id="cropper">
      <div class="ic-crop-area" ref={cropAreaRef}>
        <Show when={src()}>
          <img
            ref={imgRef}
            src={src()}
            onLoad={handleImageLoad}
            draggable={false}
          />
          <div class="ic-crop-overlay">
            <div
              class="ic-crop-circle"
              style={{
                left: `${crop().x}%`,
                top: `${crop().y}%`,
                width: `${cropToPixels().size}px`,
                height: `${cropToPixels().size}px`,
              }}
              onMouseDown={handleCropMouseDown}
            >
              <div class="ic-crop-handle" onMouseDown={handleResizeMouseDown} />
            </div>
          </div>
        </Show>
      </div>
      <div class="ic-preview-area">
        <Spinner show={previewLoading()} />
        <Show when={showPreview() && previewSrc()}>
          <img
            class="ic-preview-img"
            src={previewSrc()}
            width="100"
            height="100"
          />
        </Show>
      </div>
    </div>
  );

  return (
    <Popup
      show={showPopup()}
      name="ImageCropper"
      heading={props.heading ?? 'Crop Profile Photo'}
      content={cropperContent()}
      primaryBtnLabel={props.saveLabel ?? 'Save Photo'}
      primaryBtnDisabled={isSaving()}
      secondaryBtnLabel={props.cancelLabel ?? 'Cancel'}
      onPrimaryClick={handleSave}
      onSecondaryClick={handleClose}
      onClose={handleClose}
    />
  );
};

export default ImageCropper;
